/**
 * AlcoaFindingsPanel - ALCOA+ Findings Detail Panel
 * ============================================================================
 * DC-514: ALCOA+ findings panel with remediation actions
 *
 * Displays:
 * - Filterable list of regulatory findings
 * - Grouped by severity or principle
 * - Detailed finding information
 * - One-click remediation buttons:
 *   - AVAIL-001 -> "Generate Data Dictionary" (AI assist)
 *   - LEG-002 -> "Map in Station 6" (navigate)
 *   - COMP-001 -> "Export Missing Value Report" (download)
 * - Regulatory citations and guidance
 *
 * Design: Professional, action-oriented panel for regulatory remediation
 *
 * @version 1.0.0
 * @created 2025-12-17
 * ============================================================================
 */

import React, { useState, useEffect, useMemo } from 'react';
import './alcoa-findings-panel.css';

// ============================================================================
// Types
// ============================================================================

export type FindingSeverity = 'critical' | 'major' | 'minor' | 'observation';
export type AlcoaPrinciple = 'attributable' | 'legible' | 'contemporaneous' | 'original' | 'accurate' | 'complete' | 'consistent' | 'enduring' | 'available';
export type EffortLevel = 'low' | 'medium' | 'high';
export type RemediationActionType = 'ai_assist' | 'navigate' | 'download' | 'generate';

export interface AlcoaFinding {
  id?: string;
  findingCode: string;
  principle: AlcoaPrinciple;
  title: string;
  description: string;
  severity: FindingSeverity;
  affectedColumns?: string[];
  affectedRowCount?: number;
  sampleValues?: unknown[];
  remediation: string;
  effortLevel?: EffortLevel;
  regulatoryCitation?: string;
  regulatoryText?: string;
  remediationActionType?: RemediationActionType;
  remediationActionTarget?: string;
  createdAt?: string;
}

export interface AlcoaFindingsPanelProps {
  /** Journey ID for API calls */
  journeyId: string;
  /** Asset ID (optional - for asset-specific findings) */
  assetId?: string;
  /** Pre-loaded findings (optional) */
  findings?: AlcoaFinding[];
  /** Initial filter by severity */
  initialSeverityFilter?: FindingSeverity[];
  /** Initial filter by principle */
  initialPrincipleFilter?: AlcoaPrinciple[];
  /** Callback when remediation action is clicked */
  onRemediationAction?: (finding: AlcoaFinding, action: RemediationActionType) => void;
  /** Callback to navigate to a specific station */
  onNavigateToStation?: (stationId: string) => void;
  /** Show in compact mode */
  compact?: boolean;
  /** Maximum height with scroll */
  maxHeight?: string;
}

// ============================================================================
// Constants
// ============================================================================

// Cream/teal tokens (v2.0 beat 8) — four visibly distinct hues so regulatory
// severity meaning survives the re-skin: hard red-brown · terracotta · amber ·
// slate-grey. Values flow into SVG strokes and --severity-* custom props.
const SEVERITY_CONFIG: Record<FindingSeverity, { label: string; color: string; bgColor: string; icon: string }> = {
  critical: { label: 'Critical', color: 'var(--tri-hard)', bgColor: 'var(--tri-hard-bg)', icon: '!' },
  major: { label: 'Major', color: 'var(--tri-terra)', bgColor: 'rgba(var(--tri-terra-rgb), 0.12)', icon: '!' },
  minor: { label: 'Minor', color: 'var(--tri-soft)', bgColor: 'var(--tri-soft-bg)', icon: '!' },
  observation: { label: 'Observation', color: 'var(--tri-neutral)', bgColor: 'var(--tri-neutral-bg)', icon: 'i' },
};

const PRINCIPLE_CONFIG: Record<AlcoaPrinciple, { label: string; shortLabel: string }> = {
  attributable: { label: 'Attributable', shortLabel: 'ATTR' },
  legible: { label: 'Legible', shortLabel: 'LEG' },
  contemporaneous: { label: 'Contemporaneous', shortLabel: 'CONT' },
  original: { label: 'Original', shortLabel: 'ORIG' },
  accurate: { label: 'Accurate', shortLabel: 'ACC' },
  complete: { label: 'Complete', shortLabel: 'COMP' },
  consistent: { label: 'Consistent', shortLabel: 'CONS' },
  enduring: { label: 'Enduring', shortLabel: 'END' },
  available: { label: 'Available', shortLabel: 'AVAIL' },
};

const EFFORT_CONFIG: Record<EffortLevel, { label: string; color: string }> = {
  low: { label: 'Low Effort', color: 'var(--tri-neutral)' },
  medium: { label: 'Medium Effort', color: 'var(--tri-soft)' },
  high: { label: 'High Effort', color: 'var(--tri-hard)' },
};

const REMEDIATION_ACTIONS: Record<string, { label: string; icon: string; type: RemediationActionType }> = {
  'AVAIL-001': { label: 'Generate Data Dictionary', icon: 'AI', type: 'ai_assist' },
  'AVAIL-002': { label: 'Document Access Protocol', icon: 'AI', type: 'ai_assist' },
  'LEG-002': { label: 'Map in Ontology Station', icon: '->', type: 'navigate' },
  'LEG-003': { label: 'Generate Abbreviation Guide', icon: 'AI', type: 'ai_assist' },
  'COMP-001': { label: 'Export Missing Value Report', icon: 'DL', type: 'download' },
  'COMP-003': { label: 'Export Sequence Gap Report', icon: 'DL', type: 'download' },
  'ACC-001': { label: 'Export Outlier Report', icon: 'DL', type: 'download' },
  'ACC-002': { label: 'Export Duplicate Report', icon: 'DL', type: 'download' },
  'CONS-001': { label: 'Standardize Date Formats', icon: 'FX', type: 'generate' },
  'ATTR-001': { label: 'Add Attribution Columns', icon: 'AI', type: 'ai_assist' },
  'END-001': { label: 'Convert to Open Format', icon: 'FX', type: 'generate' },
};

// ============================================================================
// Helper Functions
// ============================================================================

function sortFindingsBySeverity(findings: AlcoaFinding[]): AlcoaFinding[] {
  const severityOrder: Record<FindingSeverity, number> = {
    critical: 0,
    major: 1,
    minor: 2,
    observation: 3,
  };

  return [...findings].sort((a, b) => {
    const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
    if (severityDiff !== 0) return severityDiff;
    return a.findingCode.localeCompare(b.findingCode);
  });
}

function groupFindingsByPrinciple(findings: AlcoaFinding[]): Map<AlcoaPrinciple, AlcoaFinding[]> {
  const grouped = new Map<AlcoaPrinciple, AlcoaFinding[]>();

  for (const finding of findings) {
    const existing = grouped.get(finding.principle) || [];
    existing.push(finding);
    grouped.set(finding.principle, existing);
  }

  return grouped;
}

// ============================================================================
// Sub-Components
// ============================================================================

interface FilterBarProps {
  severityFilter: FindingSeverity[];
  principleFilter: AlcoaPrinciple[];
  onSeverityChange: (severities: FindingSeverity[]) => void;
  onPrincipleChange: (principles: AlcoaPrinciple[]) => void;
  findingCounts: { severity: Record<FindingSeverity, number>; principle: Record<AlcoaPrinciple, number> };
}

const FilterBar: React.FC<FilterBarProps> = ({
  severityFilter,
  principleFilter,
  onSeverityChange,
  onPrincipleChange,
  findingCounts,
}) => {
  const toggleSeverity = (severity: FindingSeverity) => {
    if (severityFilter.includes(severity)) {
      onSeverityChange(severityFilter.filter((s) => s !== severity));
    } else {
      onSeverityChange([...severityFilter, severity]);
    }
  };

  const togglePrinciple = (principle: AlcoaPrinciple) => {
    if (principleFilter.includes(principle)) {
      onPrincipleChange(principleFilter.filter((p) => p !== principle));
    } else {
      onPrincipleChange([...principleFilter, principle]);
    }
  };

  return (
    <div className="alcoa-filter-bar">
      <div className="filter-group">
        <span className="filter-label">Severity:</span>
        <div className="filter-buttons">
          {(Object.keys(SEVERITY_CONFIG) as FindingSeverity[]).map((severity) => {
            const config = SEVERITY_CONFIG[severity];
            const count = findingCounts.severity[severity] || 0;
            const isActive = severityFilter.length === 0 || severityFilter.includes(severity);

            return (
              <button
                key={severity}
                className={`filter-btn severity-${severity} ${isActive ? 'active' : ''}`}
                onClick={() => toggleSeverity(severity)}
                disabled={count === 0}
                style={{
                  '--severity-color': config.color,
                  '--severity-bg': config.bgColor,
                } as React.CSSProperties}
              >
                {config.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      <div className="filter-group">
        <span className="filter-label">Principle:</span>
        <div className="filter-buttons principle-filters">
          {(Object.keys(PRINCIPLE_CONFIG) as AlcoaPrinciple[]).map((principle) => {
            const config = PRINCIPLE_CONFIG[principle];
            const count = findingCounts.principle[principle] || 0;
            const isActive = principleFilter.length === 0 || principleFilter.includes(principle);

            if (count === 0) return null;

            return (
              <button
                key={principle}
                className={`filter-btn principle-btn ${isActive ? 'active' : ''}`}
                onClick={() => togglePrinciple(principle)}
                title={config.label}
              >
                {config.shortLabel} ({count})
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface FindingCardProps {
  finding: AlcoaFinding;
  onRemediationAction?: (finding: AlcoaFinding, action: RemediationActionType) => void;
  onNavigateToStation?: (stationId: string) => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

const FindingCard: React.FC<FindingCardProps> = ({
  finding,
  onRemediationAction,
  onNavigateToStation,
  expanded = false,
  onToggleExpand,
}) => {
  const severityConfig = SEVERITY_CONFIG[finding.severity];
  const principleConfig = PRINCIPLE_CONFIG[finding.principle];
  const effortConfig = finding.effortLevel ? EFFORT_CONFIG[finding.effortLevel] : null;
  const remediationAction = REMEDIATION_ACTIONS[finding.findingCode];

  const handleRemediationClick = () => {
    if (!remediationAction) return;

    if (remediationAction.type === 'navigate' && onNavigateToStation) {
      // LEG-002 navigates to Station 6 (Ontology Inference)
      onNavigateToStation('station-6');
    } else if (onRemediationAction) {
      onRemediationAction(finding, remediationAction.type);
    }
  };

  return (
    <div
      className={`alcoa-finding-card severity-${finding.severity}`}
      style={{
        '--severity-color': severityConfig.color,
        '--severity-bg': severityConfig.bgColor,
      } as React.CSSProperties}
    >
      {/* Header */}
      <div className="finding-header" onClick={onToggleExpand}>
        <div className="finding-severity-badge">
          <span className="severity-icon">{severityConfig.icon}</span>
          <span className="severity-label">{severityConfig.label}</span>
        </div>

        <div className="finding-main">
          <div className="finding-code-row">
            <span className="finding-code">{finding.findingCode}</span>
            <span className="finding-principle">{principleConfig.label}</span>
          </div>
          <h4 className="finding-title">{finding.title}</h4>
        </div>

        <button className="finding-expand-btn" aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? '-' : '+'}
        </button>
      </div>

      {/* Expandable Content */}
      {expanded && (
        <div className="finding-details">
          <p className="finding-description">{finding.description}</p>

          {/* Affected Data */}
          {(finding.affectedColumns || finding.affectedRowCount) && (
            <div className="finding-affected">
              {finding.affectedColumns && finding.affectedColumns.length > 0 && (
                <span className="affected-item">
                  <strong>Columns:</strong> {finding.affectedColumns.join(', ')}
                </span>
              )}
              {finding.affectedRowCount !== undefined && finding.affectedRowCount > 0 && (
                <span className="affected-item">
                  <strong>Rows:</strong> {finding.affectedRowCount.toLocaleString()}
                </span>
              )}
            </div>
          )}

          {/* Remediation */}
          <div className="finding-remediation">
            <h5>Remediation</h5>
            <p>{finding.remediation}</p>

            <div className="remediation-meta">
              {effortConfig && (
                <span
                  className="effort-badge"
                  style={{ color: effortConfig.color }}
                >
                  {effortConfig.label}
                </span>
              )}

              {remediationAction && (
                <button
                  className="remediation-action-btn"
                  onClick={handleRemediationClick}
                >
                  <span className="action-icon">{remediationAction.icon}</span>
                  {remediationAction.label}
                </button>
              )}
            </div>
          </div>

          {/* Regulatory Citation */}
          {finding.regulatoryCitation && (
            <div className="finding-citation">
              <h5>Regulatory Reference</h5>
              <span className="citation-code">{finding.regulatoryCitation}</span>
              {finding.regulatoryText && (
                <p className="citation-text">{finding.regulatoryText}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const AlcoaFindingsPanel: React.FC<AlcoaFindingsPanelProps> = ({
  journeyId,
  assetId,
  findings: initialFindings,
  initialSeverityFilter = [],
  initialPrincipleFilter = [],
  onRemediationAction,
  onNavigateToStation,
  compact = false,
  maxHeight,
}) => {
  const [findings, setFindings] = useState<AlcoaFinding[]>(initialFindings || []);
  const [loading, setLoading] = useState(!initialFindings);
  const [error, setError] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<FindingSeverity[]>(initialSeverityFilter);
  const [principleFilter, setPrincipleFilter] = useState<AlcoaPrinciple[]>(initialPrincipleFilter);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState<'severity' | 'principle'>('severity');

  // Fetch findings if not provided
  useEffect(() => {
    if (!initialFindings && journeyId) {
      fetchFindings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchFindings is defined below in component scope
  }, [journeyId, assetId, initialFindings]);

  const fetchFindings = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `/api/v1/journeys/${journeyId}/alcoa/findings`;
      const params = new URLSearchParams();

      if (severityFilter.length > 0) {
        params.set('severity', severityFilter.join(','));
      }
      if (principleFilter.length > 0) {
        params.set('principle', principleFilter.join(','));
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch findings: ${response.statusText}`);
      }

      const data = await response.json();
      setFindings(data.findings || []);
      setLoading(false);
    } catch (err: unknown) {
      console.error('[AlcoaFindingsPanel] Failed to fetch findings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch findings');
      setLoading(false);
    }
  };

  // Calculate finding counts for filters
  const findingCounts = useMemo(() => {
    const severity: Record<FindingSeverity, number> = {
      critical: 0,
      major: 0,
      minor: 0,
      observation: 0,
    };
    const principle: Record<AlcoaPrinciple, number> = {
      attributable: 0,
      legible: 0,
      contemporaneous: 0,
      original: 0,
      accurate: 0,
      complete: 0,
      consistent: 0,
      enduring: 0,
      available: 0,
    };

    for (const finding of findings) {
      severity[finding.severity]++;
      principle[finding.principle]++;
    }

    return { severity, principle };
  }, [findings]);

  // Filter and sort findings
  const filteredFindings = useMemo(() => {
    let filtered = findings;

    if (severityFilter.length > 0) {
      filtered = filtered.filter((f) => severityFilter.includes(f.severity));
    }

    if (principleFilter.length > 0) {
      filtered = filtered.filter((f) => principleFilter.includes(f.principle));
    }

    return sortFindingsBySeverity(filtered);
  }, [findings, severityFilter, principleFilter]);

  // Group findings if needed
  const groupedFindings = useMemo(() => {
    if (groupBy === 'principle') {
      return groupFindingsByPrinciple(filteredFindings);
    }
    return null;
  }, [filteredFindings, groupBy]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedIds(new Set(filteredFindings.map((f) => f.id || f.findingCode)));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  // Loading state
  if (loading) {
    return (
      <div className={`alcoa-findings-panel ${compact ? 'compact' : ''} loading`}>
        <div className="findings-header">
          <h3 className="findings-title">ALCOA+ Findings</h3>
        </div>
        <div className="findings-loading">
          <div className="loading-spinner" />
          <span>Loading findings...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`alcoa-findings-panel ${compact ? 'compact' : ''} error`}>
        <div className="findings-header">
          <h3 className="findings-title">ALCOA+ Findings</h3>
        </div>
        <div className="findings-error">
          <p>Failed to load findings: {error}</p>
          <button onClick={fetchFindings}>Retry</button>
        </div>
      </div>
    );
  }

  // Empty state
  if (findings.length === 0) {
    return (
      <div className={`alcoa-findings-panel ${compact ? 'compact' : ''} empty`}>
        <div className="findings-header">
          <h3 className="findings-title">ALCOA+ Findings</h3>
        </div>
        <div className="findings-empty">
          <span className="empty-icon">&#x2713;</span>
          <p>No regulatory findings detected</p>
          <span className="empty-subtext">Your data meets ALCOA+ compliance requirements</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`alcoa-findings-panel ${compact ? 'compact' : ''}`}
      style={{ maxHeight: maxHeight }}
    >
      {/* Header */}
      <div className="findings-header">
        <h3 className="findings-title">
          ALCOA+ Findings
          <span className="findings-count">{filteredFindings.length}</span>
        </h3>

        <div className="findings-actions">
          <div className="group-by-toggle">
            <button
              className={groupBy === 'severity' ? 'active' : ''}
              onClick={() => setGroupBy('severity')}
            >
              By Severity
            </button>
            <button
              className={groupBy === 'principle' ? 'active' : ''}
              onClick={() => setGroupBy('principle')}
            >
              By Principle
            </button>
          </div>

          <button className="expand-collapse-btn" onClick={expandedIds.size > 0 ? collapseAll : expandAll}>
            {expandedIds.size > 0 ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
      </div>

      {/* Filters */}
      {!compact && (
        <FilterBar
          severityFilter={severityFilter}
          principleFilter={principleFilter}
          onSeverityChange={setSeverityFilter}
          onPrincipleChange={setPrincipleFilter}
          findingCounts={findingCounts}
        />
      )}

      {/* Findings List */}
      <div className="findings-list">
        {groupBy === 'principle' && groupedFindings ? (
          // Grouped by principle
          Array.from(groupedFindings.entries()).map(([principle, principleFindings]) => (
            <div key={principle} className="findings-group">
              <h4 className="group-header">
                {PRINCIPLE_CONFIG[principle].label}
                <span className="group-count">{principleFindings.length}</span>
              </h4>
              {principleFindings.map((finding) => (
                <FindingCard
                  key={finding.id || finding.findingCode}
                  finding={finding}
                  expanded={expandedIds.has(finding.id || finding.findingCode)}
                  onToggleExpand={() => toggleExpanded(finding.id || finding.findingCode)}
                  onRemediationAction={onRemediationAction}
                  onNavigateToStation={onNavigateToStation}
                />
              ))}
            </div>
          ))
        ) : (
          // Flat list sorted by severity
          filteredFindings.map((finding) => (
            <FindingCard
              key={finding.id || finding.findingCode}
              finding={finding}
              expanded={expandedIds.has(finding.id || finding.findingCode)}
              onToggleExpand={() => toggleExpanded(finding.id || finding.findingCode)}
              onRemediationAction={onRemediationAction}
              onNavigateToStation={onNavigateToStation}
            />
          ))
        )}
      </div>

      {/* Summary Footer */}
      {!compact && (
        <div className="findings-footer">
          <div className="severity-summary">
            {findingCounts.severity.critical > 0 && (
              <span className="summary-item critical">
                {findingCounts.severity.critical} Critical
              </span>
            )}
            {findingCounts.severity.major > 0 && (
              <span className="summary-item major">
                {findingCounts.severity.major} Major
              </span>
            )}
            {findingCounts.severity.minor > 0 && (
              <span className="summary-item minor">
                {findingCounts.severity.minor} Minor
              </span>
            )}
            {findingCounts.severity.observation > 0 && (
              <span className="summary-item observation">
                {findingCounts.severity.observation} Observations
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlcoaFindingsPanel;
